export const DEFAULT_MEMBER_PROGRAM_DISCOUNT = 20;

export const isIncludedWithMembership = (program) => {
  if (!program || program.isSubscription) return false;
  if (program.includedWithMembership === true) return true;
  const amount = Number(program.amount);
  return !Number.isFinite(amount) || amount === 0;
};

const isBasicsOfBiophysics = (program) =>
  /basics of biophysics/i.test(String(program?.name || program?.courseName || ""));

/**
 * Admin flag: a $0 program non-members may open without joining.
 * Basics of Biophysics stays members-only even when the flag is on.
 */
export const isFreeForNonMembers = (program) => {
  if (!program || program.isSubscription) return false;
  if (isBasicsOfBiophysics(program)) return false;
  const flag = program.availableToFreePlan;
  const enabled = flag === true || flag === 1 || flag === "1" || flag === "true";
  return enabled && isIncludedWithMembership(program);
};

export const getMemberDiscountPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_MEMBER_PROGRAM_DISCOUNT;
  if (n > 100) return 100;
  return n;
};

export const getNonMemberPrice = (program) => {
  if (program?.nonMemberAmount != null) return Number(program.nonMemberAmount);
  return Number(program?.amount) || 0;
};

export const getMemberPrice = (program, discountPercent) => {
  if (isIncludedWithMembership(program)) return 0;
  if (program?.memberAmount != null) return Number(program.memberAmount);
  const amount = getNonMemberPrice(program);
  const pct = getMemberDiscountPercent(discountPercent);
  return Math.round(amount * (1 - pct / 100) * 100) / 100;
};

export const formatProgramPrice = (amount, currency = "usd") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const code = String(currency || "usd").toLowerCase();
  const symbol = code === "usd" ? "$" : `${code.toUpperCase()} `;
  const formatted = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return `${symbol}${formatted}`;
};

/** Decode &lt; &gt; (and double-encoded) markup from Admin / LMS HTML. */
export const decodeMaybeEncodedHtml = (value) => {
  let html = String(value || "");
  if (!html) return "";
  for (let i = 0; i < 3; i += 1) {
    if (!html.includes("&lt;") && !html.includes("&amp;") && !html.includes("&#")) {
      break;
    }
    if (typeof document === "undefined") {
      const next = html
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&");
      if (next === html) break;
      html = next;
      continue;
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    if (textarea.value === html) break;
    html = textarea.value;
  }
  return html;
};

export const stripHtml = (value) =>
  decodeMaybeEncodedHtml(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeCopy = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&nbsp;/gi, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isTitleOnlyCopy = (text, title) => {
  const copy = normalizeCopy(text);
  const name = normalizeCopy(title);
  return Boolean(copy && name && copy === name);
};

const omitRepeatedTitle = (root, title) => {
  if (!root || !title) return;
  root.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((el) => {
    if (isTitleOnlyCopy(el.textContent, title)) {
      el.remove();
    }
  });
  let first = root.firstElementChild;
  while (first && isTitleOnlyCopy(first.textContent, title)) {
    first.remove();
    first = root.firstElementChild;
  }
  Array.from(
    root.querySelectorAll("p, div, span, strong, em, b, i, center")
  )
    .reverse()
    .forEach((el) => {
      if (isTitleOnlyCopy(el.textContent, title) && !el.querySelector("img")) {
        el.remove();
      }
    });
};

/** HTML for the Details modal: real markup, no buy buttons. */
export const programDetailsHtml = (
  value,
  { stripImages = false, omitTitle = "" } = {}
) => {
  const decoded = decodeMaybeEncodedHtml(value);
  if (!decoded) return "";
  if (typeof DOMParser === "undefined") {
    return decoded.replace(/<script[\s\S]*?<\/script>/gi, "");
  }
  const doc = new DOMParser().parseFromString(decoded, "text/html");
  doc
    .querySelectorAll("script, style, button, .buy-now-button")
    .forEach((el) => el.remove());
  doc.querySelectorAll("img").forEach((img) => {
    if (stripImages) {
      img.remove();
      return;
    }
    img.removeAttribute("width");
    img.removeAttribute("height");
    img.style.maxWidth = "100%";
    img.style.height = "auto";
    img.style.display = "block";
    img.style.margin = "12px auto";
  });
  if (omitTitle) {
    omitRepeatedTitle(doc.body, omitTitle);
  }
  doc.body.querySelectorAll("*").forEach((el) => {
    el.removeAttribute("align");
    if (el.style) {
      el.style.textAlign = "left";
      el.style.maxWidth = "100%";
      el.style.marginLeft = "0";
      el.style.marginRight = "0";
      el.style.float = "none";
      el.style.fontSize = "";
    }
  });
  return String(doc.body?.innerHTML || "").trim();
};

/** First description line + remaining HTML (includes, etc.). */
export const splitProgramDetails = (
  value,
  { stripImages = false, omitTitle = "" } = {}
) => {
  const html = programDetailsHtml(value, { stripImages, omitTitle });
  if (!html) return { lead: "", bodyHtml: "" };
  if (typeof DOMParser === "undefined") {
    return { lead: stripHtml(html), bodyHtml: "" };
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const takeLeadNode = (root) => {
    let node = root.firstElementChild;
    while (
      node &&
      /^(div|section|article|span)$/i.test(node.tagName) &&
      node.querySelector(":scope > p, :scope > a")
    ) {
      const inner = node.querySelector(":scope > p, :scope > a");
      if (inner) return inner;
      node = node.firstElementChild;
    }
    if (node && /^(p|a|h1|h2|h3|h4)$/i.test(node.tagName)) return node;
    const firstP = root.querySelector("p");
    if (firstP && !firstP.closest("ul, ol")) return firstP;
    return null;
  };
  const leadNode = takeLeadNode(doc.body);
  let lead = "";
  if (leadNode && !/^(ul|ol)$/i.test(leadNode.tagName)) {
    lead = String(leadNode.textContent || "").replace(/\s+/g, " ").trim();
    leadNode.remove();
  }
  return {
    lead,
    bodyHtml: String(doc.body?.innerHTML || "").trim(),
  };
};

export const programTopic = (program) => {
  const names = program?.wordpressCategoryName;
  const raw = Array.isArray(names)
    ? String(names[0] || "")
    : typeof names === "string"
      ? names.split(",")[0]
      : "";
  const topic = raw.trim();
  if (!topic || /^default category$/i.test(topic)) return "";
  return topic;
};

export const programTeacher = (program) => {
  const teacher =
    program?.teacher ||
    program?.teacherName ||
    program?.instructor ||
    "";
  return String(teacher).trim();
};
